import { Switch } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { type BusinessHoursDay, DEFAULT_BUSINESS_HOURS } from "@msuite/utils/incoming-invoices";
import { useId } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { BusinessHoursDayRow } from "./business-hours-day-row";
import type { BusinessHoursFormValues } from "./business-hours-form-values";
import { BusinessHoursSummary } from "./business-hours-summary";
import { WEEKDAY_TRANSLATION_KEYS } from "./weekday-translation-keys";

/** Props Interface */
interface BusinessHoursEditorProps {
	/**
	 * The hours this level takes while its own field is null. Passing them adds the "own hours"
	 * switch — a workspace inherits the organisation's; the organisation itself passes nothing.
	 */
	inheritedHours?: Array<BusinessHoursDay>;
}

/**
 * Edits the `businessHours` field of the surrounding form: one row per weekday, a closed day
 * absent from the array. The array is kept sorted by weekday, so a row's inputs stay at its index.
 */
export function BusinessHoursEditor({ inheritedHours }: BusinessHoursEditorProps) {
	/** Context */
	const { t } = useTranslation();
	const methods = useFormContext<BusinessHoursFormValues>();
	const businessHours = useWatch({ control: methods.control, name: "businessHours" });

	/** Hooks */
	const ownHoursId = useId();

	/** Functions */
	function setBusinessHours(next: BusinessHoursFormValues["businessHours"]) {
		methods.setValue("businessHours", next, { shouldDirty: true, shouldValidate: true });
	}

	function handleOnToggleOwnHours(isOwn: boolean) {
		setBusinessHours(isOwn ? copyDays(inheritedHours ?? DEFAULT_BUSINESS_HOURS) : null);
	}

	function handleOnToggleDay(weekday: number, isOpen: boolean) {
		const days = (businessHours ?? []).filter((day) => day.weekday !== weekday);
		if (isOpen) days.push(getOpeningDay(weekday));
		days.sort((a, b) => a.weekday - b.weekday);
		setBusinessHours(days);
	}

	function findIndex(weekday: number) {
		const days = businessHours ?? [];
		for (let index = 0; index < days.length; index++) {
			if (days[index]?.weekday === weekday) return index;
		}
		return null;
	}

	/** Computed */
	const canInherit = inheritedHours !== undefined;
	const isInherited = canInherit && businessHours === null;

	/** Render */
	return (
		<div className="v-stack gap-4">
			{canInherit && (
				<div className="h-stack gap-2">
					<Switch
						id={ownHoursId}
						checked={!isInherited}
						onCheckedChange={handleOnToggleOwnHours}
						data-cy="business-hours-own-switch"
					/>
					<label
						htmlFor={ownHoursId}
						className="text-sm cursor-pointer select-none"
					>
						{t("organization.own_business_hours")}
					</label>
				</div>
			)}
			{isInherited ? (
				<div className="v-stack gap-2">
					<p className="label">{t("organization.business_hours_inherited_hint")}</p>
					<BusinessHoursSummary hours={inheritedHours ?? DEFAULT_BUSINESS_HOURS} />
				</div>
			) : (
				<div className="v-stack gap-2">
					{WEEKDAY_TRANSLATION_KEYS.map((key, weekday) => (
						<BusinessHoursDayRow
							key={key}
							weekday={weekday}
							index={findIndex(weekday)}
							onToggle={handleOnToggleDay}
						/>
					))}
				</div>
			)}
		</div>
	);
}

/** The hours a day opens with when switched on: the default calendar's first opening. */
function getOpeningDay(weekday: number): BusinessHoursDay {
	const template = DEFAULT_BUSINESS_HOURS[0];
	return { weekday, startsAt: template?.startsAt ?? "07:00", endsAt: template?.endsAt ?? "17:00" };
}

function copyDays(days: Array<BusinessHoursDay>) {
	return days.map((day) => ({ ...day }));
}
